import { DeviceConfigParam, DeviceConfigParamSet, DeviceConfigParamType, DeviceConfigParamValueType } from '@moderepo/mode-apis';


/**
 * This is the structure of the user input values for the device config. This structure mapped to the structure of the Device Config Schema with
 * a small difference. The DCS is an ARRAY of parameter sets and each parameter set contain an array of params. Like this
 *      DCS = [{
 *          name: "Gateway Settings",
 *          parameters: [{
 *              key: "portNumber"
 *          }, {
 *              key: "ip"
 *          }]
 *      }, {
 *          name: "File Transfer Settings",
 *          repeatable: true,
 *          parameters: [{
 *              key: "fileFormat"
 *          }, {
 *              key: "fileSize"
 *          }]
 *      }]
 *
 * Therefore, the user input values will be stored under similar structure and in the SAME ORDER as the schema
 *      deviceConfigInputs = [{
 *          portNumber: 80,
 *          ip: "127.0.0.1"
 *      }, {
 *          fileFormat: ".log",
 *          fileSize: 100
 *      }]
 *
 * However, because some parameter set are repeatable, it is possible that there can be more than 1 instances of user inputs for the same
 * parameter set. Therefore, for each parameter set, we will store array of input values like this
 *      deviceConfigInputs = [
 *          [
 *              // Even though this parameter set is NOT repeatable, we will also store paramSet inputs inside an array to be consistent
 *              // This paramSet array will just going to have only 1 instance of paramSet inputs
 *              {
 *                  portNumber: 80,
 *                  ip: "127.0.0.1"
 *              }
 *          ],
 *          [
 *              // This parameter set is repeatable therefore, it can have array of paramSet inputs, 1 for each instance
 *              {
 *                  fileFormat: ".log",
 *                  fileSize: 100
 *              },
 *              {
 *                  fileFormat: ".zip",
 *                  fileSize: 500
 *              }
 *          ]
 *      ]
 */
export type DeviceConfigInputs = {
    [paramKey: string]: DeviceConfigParamValueType | undefined;
}[][];


/**
 * The structure for the inputs' error which will be exactly the same as the inputs except that the values will be strings or undefined
 */
export type DeviceConfigInputErrors = {
    [paramKey: string]: string | undefined;
}[][];


/**
 * Convert an actual paramSet config value stored in DDP to value that we can display in the input control. Most of the input controls display the
 * value as STRINGs. This include param of type NUMBER, STRING, ARRAY and OBJECT. For NUMBER param type, we can just convert the
 * actual value to string. For param of type ARRAY and OBJECT, we need to use JSON.stringify to convert the value from [] and {} into
 * strings.
 */
const deviceConfigParamSetConfigValueToInputValue = (
    paramSettings: DeviceConfigParam<DeviceConfigParamValueType>,
    paramValue: DeviceConfigParamValueType | null | undefined,
) => {
    if (paramValue === undefined || paramValue === null) {
        return paramValue;
    }
    if (paramSettings.type === DeviceConfigParamType.NUMBER) {
        return paramValue.toString();
    }
    if (paramSettings.type === DeviceConfigParamType.ARRAY || paramSettings.type === DeviceConfigParamType.OBJECT) {
        return JSON.stringify(paramValue, undefined, 4);
    }

    // For STRING and BOOLEAN, we leave them as is.
    return paramValue;
};


/**
 * Helper function to create and initialize the inputs instance for 1 paramSet with paramSet's defaultValue or existing paramSet config. E.g.
 *
 * Input
 *      paramSet = [{
 *          key: "portNumber",
 *          defaultValue: 80,
 *      }, {
 *          key: "ip",
 *      }, {
 *          key: "protocol",
 *          defaultValue: "https"
 *      }]
 *
 *      existingParamSetConfig = {
 *          portNumber: 8080,
 *          ip: "127.0.0.1"
 *      }
 *
 *  Return
 *      {
 *          portNumber: 8080,           // Use the existingParamSetConfig instead of defaultValue if existingParamSetConfig is provided
 *          ip: "127.0.0.1",
 *          defaultValue: "https"       // Use defaultValue because existingParamSetConfig doesn't have "protocol" set
 *      }
 */
export const getInitialDeviceConfigParamSetInputs = (
    deviceId: number,
    paramSet: DeviceConfigParamSet,
    existingParamSetConfig: {
        [paramKey: string]: DeviceConfigParamValueType | undefined;
    } | undefined,
): {
    [paramKey: string]: DeviceConfigParamValueType | undefined;
} => {
    const paramSetInputs = {};
    paramSet.parameters.forEach((param) => {
        const existingValue = deviceConfigParamSetConfigValueToInputValue(param, existingParamSetConfig?.[param.key]);

        // Get the stringified defaultValue if there is a defaultValue AND if param's type is OBJECT or ARRAY
        const defaultValue = param.defaultValue && (param.type === DeviceConfigParamType.ARRAY || param.type === DeviceConfigParamType.OBJECT)
            ? JSON.stringify(param.defaultValue, undefined, 4) : param.defaultValue;

        // If defaultValue is a string, check if it contains {{_deviceId_}} and replace all the {{_deviceId_}} with the actual deviceId
        const defaultValueWithVariablesReplaced = typeof defaultValue === 'string'
            ? defaultValue.replaceAll(/{{\s*_deviceId_\s*}}/g, deviceId.toString())
            : defaultValue;

        paramSetInputs[param.key] = existingValue ?? defaultValueWithVariablesReplaced;
    });
    return paramSetInputs;
};


/**
 * Helper function to create a DeviceConfigInput and initialize it with DEFAULT values or existingDeviceConfig if existingDeviceConfig is provided
 */
export const getInitialDeviceConfigInputs = (
    deviceId: number,
    parameterSet: readonly DeviceConfigParamSet[] | undefined,
    existingDeviceConfig: DeviceConfigInputs | undefined,
): DeviceConfigInputs => {

    const deviceConfig: DeviceConfigInputs = [];

    parameterSet?.forEach((paramSet, paramSetIndex) => {
        /**
         * Create an array of paramSetInputs and add it to the deviceConfig
         * The paramSetInputsInstances is an array of {} e.g.
         *      [{
         *          fileFormat: ".log",
         *          fileSize: 100
         *      }, {
         *          fileFormat: ".zip",
         *          fileSize: 500
         *      }]
         * Even for paramSet that are NOT repeatable, we will still create an array of inputs instances. The array will just going to have 1 instance
         */
        const paramSetInputsInstances: {}[] = [];
        deviceConfig.push(paramSetInputsInstances);

        const existingParamSetInstancesConfig = existingDeviceConfig?.[paramSetIndex];
        if (!paramSet.repeatable) {
            // Create inputs for 1 paramSet and add it to the paramSetInputsInstances IF the paramSet is NOT repeatable.
            // Initialize the inputs with existingParamSetInstancesConfig if there is one
            const paramSetInputs = getInitialDeviceConfigParamSetInputs(deviceId, paramSet, existingParamSetInstancesConfig?.[0]);
            paramSetInputsInstances.push(paramSetInputs);
        } else {
            // For repeatable paramSet, we don't need to create instance input for the paramSet. We will create them when the user click "Add"
            // paramSet instance. However, if there are existing configs for the paramSet instances, then we will create 1 instance inputs
            // for each config
            const paramSetInstancesInputs = existingParamSetInstancesConfig?.map((paramSetInstanceValue) => {
                return getInitialDeviceConfigParamSetInputs(deviceId, paramSet, paramSetInstanceValue);
            });
            paramSetInputsInstances.push(...(paramSetInstancesInputs ?? []));
        }
    });

    return deviceConfig;
};



/**
 * Helper function to cast the user inputs to the correct type. Because we use <input /> element to get user input, the input value we
 * get will always going to be string, even if we set the type="number". Therefore, we need to go through all the input values and cast
 * them to the correct type based on the schema. For example
 *
 * Input:
 *      deviceConfigInputs = [[{
 *          port: "80",                     // The param type is NUMBER but the input value is a STRING
 *          ip: "127.0.0.1",
 *          fileTypes: '[".log",".txt"]'    // The param type is ARRAY but the value is a STRING
 *      }]]
 *
 * Output:
 *      deviceConfigInputs = [[{
 *          port: 80,
 *          ip: "127.0.0.1"
 *          fileTypes: [".log",".txt"]      // Parse the string into ARRAY
 *      }]]
 */
export const normalizeDeviceConfigInputs = (
    deviceConfigInputs: DeviceConfigInputs, deviceConfigSchema: readonly DeviceConfigParamSet[] | undefined,
    skipArrayAndObjectsType: boolean = false,
): DeviceConfigInputs => {
    if (!deviceConfigSchema || !deviceConfigInputs) {
        return deviceConfigInputs;
    }

    const fixedDeviceConfigInputs = deviceConfigInputs.map((paramSetInputs, paramSetIndex) => {
        return paramSetInputs.map((paramSetInstanceInputs) => {
            const fixedParamSetInstanceInputs = {};

            // For each attribute in the paramSetInstanceInputs, check the type of value it is based on the schema and then cast the value
            // to the correct type. We only need to handle NUMBER and BOOLEAN types. STRING, ARRAY, OBJECT can be kept as strings.
            Object.entries(paramSetInstanceInputs).forEach(([paramKey, value]) => {

                // Get the schema for the paramSet and then the schema for the param
                const paramSetSchema = deviceConfigSchema[paramSetIndex];
                const paramConfig = paramSetSchema.parameters.find((param) => {
                    return param.key === paramKey;
                });

                if (paramConfig && paramConfig.type === DeviceConfigParamType.NUMBER && value !== undefined) {
                    fixedParamSetInstanceInputs[paramKey] = Number(value);
                } else if (paramConfig && paramConfig.type === DeviceConfigParamType.BOOLEAN && value !== undefined) {
                    fixedParamSetInstanceInputs[paramKey] = Boolean(value);
                } else if (paramConfig && !skipArrayAndObjectsType && value !== undefined
                    && (paramConfig.type === DeviceConfigParamType.OBJECT || paramConfig.type === DeviceConfigParamType.ARRAY)
                ) {
                    try {
                        fixedParamSetInstanceInputs[paramKey] = JSON.parse(value as string);
                    } catch (error) {
                        // This shouldn't happen because this function should only be called AFTER the inputs are validated
                        // eslint-disable-next-line no-console
                        console.error(error);
                    }
                } else {
                    // Keep the value as is, don't need to cast the value. Only trim it if it is a string
                    fixedParamSetInstanceInputs[paramKey] = typeof value === 'string' ? value.trim() : value;
                }
            });

            return fixedParamSetInstanceInputs;
        });
    });

    return fixedDeviceConfigInputs;
};



/**
 * Create and add a new paramSet inputs instance for a paramSet
 */
export const addDeviceConfigParamSetInputsInstance = (
    deviceId: number,
    paramSetIndex: number,
    currentDeviceConfigInputs: DeviceConfigInputs,
    deviceConfigSchema: readonly DeviceConfigParamSet[] | undefined,
): DeviceConfigInputs | undefined => {
    const paramSetSchema = deviceConfigSchema?.[paramSetIndex];

    if (paramSetSchema && paramSetSchema.repeatable) {
        // Create a new deviceConfigInputs object by making copy of the current deviceConfigInputs
        const newDeviceConfigInputs: DeviceConfigInputs = JSON.parse(JSON.stringify(currentDeviceConfigInputs));

        // Create a new array to store the paramSet inputs instances if it does not exist
        if (!newDeviceConfigInputs[paramSetIndex]) {
            newDeviceConfigInputs[paramSetIndex] = [];
        }

        newDeviceConfigInputs[paramSetIndex].push(getInitialDeviceConfigParamSetInputs(deviceId, paramSetSchema, undefined));
        return newDeviceConfigInputs;
    }
    return undefined;
};


/**
 * Remove a paramSet inputs instance at the given paramSetIndex and paramSetInstanceIndex
 */
export const removeDeviceConfigParamSetInputsInstance = (
    paramSetIndex: number,
    paramSetInstanceIndex: number,
    currentDeviceConfigInputs: DeviceConfigInputs,
): DeviceConfigInputs | undefined => {

    if (currentDeviceConfigInputs[paramSetIndex] && currentDeviceConfigInputs[paramSetIndex][paramSetInstanceIndex]) {
        // Remove 1 item at paramSetInstanceIndex
        const newInstancesArray = currentDeviceConfigInputs[paramSetIndex].filter((instance, index) => {
            return index !== paramSetInstanceIndex;
        });

        // Create a new deviceConfigInputs by making copy of the current deviceConfigInputs and replace the param set input instances
        // array at "paramSetIndex"
        const newDeviceConfigInputs: DeviceConfigInputs = JSON.parse(JSON.stringify(currentDeviceConfigInputs));
        newDeviceConfigInputs[paramSetIndex] = newInstancesArray;
        return newDeviceConfigInputs;
    }

    return undefined;
};


/**
 * Remove a paramSet inputs instance errors at the given paramSetIndex and paramSetInstanceIndex
 */
export const removeDeviceConfigParamSetInputErrorsInstance = (
    paramSetIndex: number,
    paramSetInstanceIndex: number,
    currentDeviceConfigInputErrors: DeviceConfigInputErrors | undefined,
): DeviceConfigInputErrors | undefined => {

    if (currentDeviceConfigInputErrors?.[paramSetIndex] && currentDeviceConfigInputErrors[paramSetIndex][paramSetInstanceIndex]) {
        // Remove 1 item at paramSetInstanceIndex
        const newInstancesArray = currentDeviceConfigInputErrors[paramSetIndex].filter((instance, index) => {
            return index !== paramSetInstanceIndex;
        });

        // Create a new deviceConfigInputs by making copy of the current deviceConfigInputs and replace the param set input instances
        // array at "paramSetIndex"
        const newDeviceConfigInputErrors: DeviceConfigInputErrors = JSON.parse(JSON.stringify(currentDeviceConfigInputErrors));
        newDeviceConfigInputErrors[paramSetIndex] = newInstancesArray;
        return newDeviceConfigInputErrors;
    }

    return undefined;
};


/**
 * When one of the param input changed, we will update the deviceConfigInput state
 */
export const updateDeviceConfigInput = (
    inputName: string,
    value: any,
    paramSetIndex: number,
    paramSetInstanceIndex: number,
    currentDeviceConfigInputs: DeviceConfigInputs,
): DeviceConfigInputs => {

    // Create a new deviceConfigInputs by making copy of the current deviceConfigInputs
    const newDeviceConfigInputs: DeviceConfigInputs = JSON.parse(JSON.stringify(currentDeviceConfigInputs));

    // Create a new array to store the paramSet input instances if it does not exist
    if (!newDeviceConfigInputs[paramSetIndex]) {
        newDeviceConfigInputs[paramSetIndex] = [];
    }

    if (!newDeviceConfigInputs[paramSetIndex][paramSetInstanceIndex]) {
        newDeviceConfigInputs[paramSetIndex][paramSetInstanceIndex] = {};
    }

    // Then replace the param at the given indices with the new value and update the state
    newDeviceConfigInputs[paramSetIndex][paramSetInstanceIndex][inputName] = value;
    return newDeviceConfigInputs;
};
